import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import AdminNavbar from "./AdminNavbar";

const API_URL = process.env.REACT_APP_API_URL;

const EMPTY_FORM = { title: "", description: "", date: "", youtubeUrl: "" };

const inputStyle = {
  width: "100%",
  fontFamily: "inherit",
  fontSize: "0.88rem",
  background: "var(--bg)",
  border: "1px solid var(--line-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "0.45rem 0.6rem",
  color: "var(--ink)",
  outline: "none",
};

export default function ManageVideos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  // Inline edit state
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/videos`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.id]: e.target.value });

  const handleAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setForm(EMPTY_FORM);
      await load();
      Swal.fire({ icon: "success", title: "Added.", confirmButtonColor: "#111111" });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to add", confirmButtonColor: "#111111" });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this film?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#111111",
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/videos/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setItems(items.filter((i) => i._id !== id));
      Swal.fire({ icon: "success", title: "Deleted.", confirmButtonColor: "#111111" });
    } catch {
      Swal.fire({ icon: "error", title: "Delete failed", confirmButtonColor: "#111111" });
    }
  };

  const handleEditOpen = (v) => {
    setEditId(v._id);
    setEditForm({
      title: v.title || "",
      description: v.description || "",
      date: v.date ? v.date.substring(0, 10) : "",
      youtubeUrl: v.youtubeUrl || "",
    });
  };

  const handleEditCancel = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditSave = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/videos/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setItems(items.map((i) => (i._id === id ? updated : i)));
      setEditId(null);
      setEditForm(EMPTY_FORM);
      Swal.fire({ icon: "success", title: "Updated.", confirmButtonColor: "#111111" });
    } catch {
      Swal.fire({ icon: "error", title: "Update failed", confirmButtonColor: "#111111" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-shell">
      <AdminNavbar />

      <div className="admin-page">
        <div className="admin-head">
          <div>
            <p className="eyebrow">Moving Image</p>
            <h1>Manage <em>films</em>.</h1>
          </div>
        </div>

        {/* ── Add form ── */}
        <form className="admin-form" onSubmit={handleAdd} style={{ marginBottom: "4rem" }}>
          <div className="field">
            <label htmlFor="title">Title</label>
            <input id="title" type="text" placeholder="Film title"
              value={form.title} onChange={handleChange} required />
          </div>
          <div className="field">
            <label htmlFor="description">Description</label>
            <textarea id="description" rows="3" placeholder="Short description..."
              value={form.description} onChange={handleChange} required />
          </div>
          <div className="field">
            <label htmlFor="date">Date</label>
            <input id="date" type="date" value={form.date} onChange={handleChange} required />
          </div>
          <div className="field">
            <label htmlFor="youtubeUrl">YouTube URL</label>
            <input id="youtubeUrl" type="url" placeholder="https://youtube.com/watch?v=..."
              value={form.youtubeUrl} onChange={handleChange} required />
          </div>
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? "Adding..." : "Add Film →"}
          </button>
        </form>

        {/* ── List ── */}
        {loading ? (
          <div className="loader-wrap"><div className="loader" /></div>
        ) : items.length === 0 ? (
          <p className="empty-state">No films added yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>URL</th>
                <th>Date</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <React.Fragment key={v._id}>
                  {/* ── Normal display row ── */}
                  <tr>
                    <td style={{ fontWeight: 500 }}>{v.title}</td>
                    <td>
                      <a href={v.youtubeUrl} target="_blank" rel="noopener noreferrer"
                        style={{ color: "var(--accent)" }}>
                        Open ↗
                      </a>
                    </td>
                    <td>
                      {v.date
                        ? new Date(v.date).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {editId !== v._id ? (
                        <>
                          <button
                            className="btn-outline"
                            style={{ padding: "0.45rem 0.95rem", fontSize: "0.72rem", marginRight: "0.5rem" }}
                            onClick={() => handleEditOpen(v)}
                          >
                            Edit
                          </button>
                          <button className="btn-delete" onClick={() => handleDelete(v._id)}>
                            Delete
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn-save"
                            style={{ padding: "0.45rem 0.95rem", fontSize: "0.72rem", marginRight: "0.5rem" }}
                            onClick={() => handleEditSave(v._id)}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save"}
                          </button>
                          <button
                            className="btn-delete"
                            onClick={handleEditCancel}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {/* ── Inline edit row (expands below) ── */}
                  {editId === v._id && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          background: "var(--bg-alt)",
                          padding: "1.25rem 1rem",
                          borderTop: "1px solid var(--line)",
                        }}
                      >
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "1rem 1.5rem",
                        }}>
                          <div className="field" style={{ margin: 0 }}>
                            <label style={{ fontSize: "0.68rem" }}>Title</label>
                            <input
                              style={inputStyle}
                              type="text"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                            />
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label style={{ fontSize: "0.68rem" }}>Date</label>
                            <input
                              style={inputStyle}
                              type="date"
                              value={editForm.date}
                              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                            />
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label style={{ fontSize: "0.68rem" }}>YouTube URL</label>
                            <input
                              style={inputStyle}
                              type="url"
                              value={editForm.youtubeUrl}
                              onChange={(e) => setEditForm({ ...editForm, youtubeUrl: e.target.value })}
                            />
                          </div>
                          <div className="field" style={{ margin: 0 }}>
                            <label style={{ fontSize: "0.68rem" }}>Description</label>
                            <textarea
                              style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
                              rows={2}
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
